// ══════════════════════════════════════════════════════════════════════════════
// COCKPIT PRO - Dashboard Principal (État propre - Nouvel utilisateur)
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Wand2, Sparkles, ArrowRight, Store, FileText, Users,
  Package, HardHat, MessageCircle, Target, BarChart2,
  Rocket, CheckCircle, Star, Crown, Zap
} from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT CARTE ACTION RAPIDE
// ══════════════════════════════════════════════════════════════════════════════

function QuickActionCard({ icon: Icon, title, description, link, color, delay }) {
  const colorStyles = {
    orange: { bg: 'bg-orange-50', iconBg: 'bg-orange-100', iconColor: 'text-orange-600', border: 'border-orange-200' },
    blue: { bg: 'bg-blue-50', iconBg: 'bg-blue-100', iconColor: 'text-blue-600', border: 'border-blue-200' },
    green: { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', border: 'border-emerald-200' },
    purple: { bg: 'bg-purple-50', iconBg: 'bg-purple-100', iconColor: 'text-purple-600', border: 'border-purple-200' },
  };
  
  const style = colorStyles[color] || colorStyles.blue;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
    >
      <Link to={link}>
        <div className={`${style.bg} border ${style.border} rounded-2xl p-5 hover:shadow-md transition-all group`}>
          <div className={`w-12 h-12 ${style.iconBg} rounded-xl flex items-center justify-center mb-4`}>
            <Icon className={`w-6 h-6 ${style.iconColor}`} />
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
          <p className="text-sm text-gray-600 mb-3">{description}</p>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 group-hover:text-gray-900">
            Commencer
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

export default function CockpitProDashboard() {
  const [greeting, setGreeting] = useState('');
  const [userName, setUserName] = useState('');
  
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Bonjour');
    else if (hour < 18) setGreeting('Bon après-midi');
    else setGreeting('Bonsoir');
    
    // Essayer de récupérer le nom de l'utilisateur
    const storedUser = localStorage.getItem('user_profile');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserName(user.company_name || user.name || '');
      } catch (e) {
        // Ignorer
      }
    }
  }, []);

  const quickActions = [
    { 
      icon: Store, 
      title: 'Configurer ma boutique', 
      description: 'Ajoute ton logo, tes infos et personnalise ta vitrine pro',
      link: '/cockpit-pro/boutique',
      color: 'orange'
    },
    { 
      icon: Package, 
      title: 'Ajouter mes prestations', 
      description: 'Crée ton catalogue de services et tarifs',
      link: '/cockpit-pro/catalogue/prestations',
      color: 'blue'
    },
    { 
      icon: FileText, 
      title: 'Créer un devis', 
      description: 'Génère des devis professionnels en quelques clics',
      link: '/cockpit-pro/commercial/devis',
      color: 'green'
    },
    { 
      icon: Users, 
      title: 'Importer mes clients', 
      description: 'Ajoute tes contacts et clients existants',
      link: '/cockpit-pro/commercial/clients',
      color: 'purple'
    },
  ];

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* HERO BIENVENUE */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-gradient-to-br from-[#1E3A5F] via-[#2d4a6f] to-[#1E3A5F] rounded-3xl p-8 text-white"
      >
        {/* Background decorations */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <span className="text-4xl">👋</span>
            </motion.div>
          </div>
          
          <h1 className="text-3xl font-bold mb-2 text-white">
            {greeting}{userName ? `, ${userName}` : ''} !
          </h1>
          
          <p className="text-white/80 text-lg max-w-xl">
            Bienvenue dans ton Cockpit Pro. C'est ici que tu vas gérer toute ton activité : boutique, clients, devis, chantiers...
          </p>
          
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/cockpit-pro/wizard"
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#F97316] hover:bg-[#ea6c0a] text-white font-semibold rounded-xl transition-colors"
            >
              <Wand2 size={20} />
              Lancer l'assistant de configuration
            </Link>
            <Link
              to="/cockpit-pro/assistant"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-colors border border-white/20"
            >
              <Sparkles size={20} />
              Parler à Abra
            </Link>
          </div>
        </div>
      </motion.div>
      
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ACTIONS RAPIDES */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Rocket size={20} className="text-[#F97316]" />
          Par où commencer ?
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <QuickActionCard 
              key={action.title}
              {...action}
              delay={0.1 + index * 0.1}
            />
          ))}
        </div>
      </div>
      
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* CHECKLIST DE DÉMARRAGE */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.5 }}
        className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle size={20} className="text-emerald-500" />
          Ta checklist de démarrage
        </h2>
        
        <div className="space-y-3">
          {[
            { label: 'Compléter les informations de ta boutique', link: '/cockpit-pro/boutique/informations', done: false },
            { label: 'Ajouter ton logo et ta couverture', link: '/cockpit-pro/boutique/visuels', done: false },
            { label: 'Créer ta première prestation', link: '/cockpit-pro/catalogue/prestations', done: false },
            { label: 'Configurer tes mentions légales (devis/factures)', link: '/cockpit-pro/boutique/mentions-legales', done: false },
            { label: 'Ajouter ton premier client', link: '/cockpit-pro/commercial/clients', done: false },
          ].map((item, index) => (
            <Link
              key={index}
              to={item.link}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
            >
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                item.done 
                  ? 'border-emerald-500 bg-emerald-500' 
                  : 'border-gray-300 group-hover:border-[#F97316]'
              }`}>
                {item.done && <CheckCircle size={14} className="text-white" />}
              </div>
              <span className={`flex-1 text-sm ${item.done ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                {item.label}
              </span>
              <ArrowRight size={16} className="text-gray-400 group-hover:text-[#F97316] group-hover:translate-x-1 transition-all" />
            </Link>
          ))}
        </div>
      </motion.div>
      
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* BLOC OFFRE FONDATEUR */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.6 }}
        className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Crown size={28} className="text-yellow-300" />
            </div>
            <div>
              <h3 className="text-lg font-bold flex items-center gap-2">
                Offre Membre Fondateur
                <Sparkles size={18} className="text-yellow-200" />
              </h3>
              <p className="text-white/80 text-sm mt-1">
                Double tes AbracaCoins chaque mois — à vie ! Plus que quelques places...
              </p>
            </div>
          </div>
          
          <Link
            to="/cockpit-pro/abracacoins"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-emerald-600 font-semibold rounded-xl hover:bg-white/90 transition-colors"
          >
            <Zap size={18} />
            En savoir plus
          </Link>
        </div>
      </motion.div>
      
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* STATS VIDES (pour montrer la structure) */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.7 }}
        className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart2 size={20} className="text-[#1E3A5F]" />
          Ton activité
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Chiffre d\'affaires', value: '0 €', icon: '💰' },
            { label: 'Devis envoyés', value: '0', icon: '📄' },
            { label: 'Clients', value: '0', icon: '👥' },
            { label: 'Chantiers', value: '0', icon: '🏗️' },
          ].map((stat, index) => (
            <div key={index} className="text-center p-4 bg-gray-50 rounded-xl">
              <span className="text-2xl mb-2 block">{stat.icon}</span>
              <p className="text-2xl font-bold text-gray-300">—</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
        
        <p className="text-center text-sm text-gray-500 mt-4">
          Tes statistiques apparaîtront ici une fois que tu auras commencé à utiliser ton Cockpit.
        </p>
      </motion.div>
      
    </div>
  );
}
